import {
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  Typography,
} from "@mui/material";
import { useNavigate } from "@tanstack/react-router";

import {
  usePilotState,
  type PilotOrigin,
} from "@/features/Generations/context/PilotStateContext";
import { generationsStepRoutePath } from "@/features/Generations/steps";

export type StartingPointStepProps = {
  siteId: string;
};

type StartingPointOption = {
  id: string;
  eyebrow: string;
  title: string;
  description: string;
  meta: string;
  cta: string;
  disabled?: boolean;
  onSelect?: () => void;
};

export function StartingPointStep({ siteId }: StartingPointStepProps) {
  const navigate = useNavigate();
  const { setPilotState } = usePilotState();

  const goToSetup = (origin: PilotOrigin) => {
    // Track how Step 2 was entered in state rather than the URL.
    setPilotState((prev) => ({ ...prev, origin }));
    void navigate({
      to: generationsStepRoutePath("setup"),
      params: { siteId, stepId: "setup" },
    });
  };

  const options: StartingPointOption[] = [
    {
      id: "match-product",
      eyebrow: "REPEAT A LOOK",
      title: "Match an existing product",
      description:
        "Recreate the scene from a product you've already shot on the canvas.",
      meta: "Opens Step 2 with no product or scene pre-set",
      cta: "Match an existing product",
      onSelect: () => goToSetup("match"),
      disabled: true,
    },
    {
      id: "blueprint",
      eyebrow: "BROWSE",
      title: "Pick from the blueprint library",
      description:
        "Best-practice style blueprints, unbranded until your brand is applied.",
      meta: "Opens Step 2 to pick a blueprint",
      cta: "Pick a blueprint",
      onSelect: () => goToSetup("blueprint"),
    },
  ];

  return (
    <Box sx={{ px: 6, py: 4 }}>
      <Typography variant="h5" gutterBottom>
        New Generation
      </Typography>
      <Stack direction={{ xs: "column", md: "row" }} spacing={3}>
        {options.map((option) => (
          <Card
            key={option.id}
            variant="outlined"
            sx={{
              flex: 1,
              opacity: option.disabled ? 0.6 : 1,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <CardContent
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                flex: 1,
              }}
            >
              <Typography variant="overline" color="text.secondary">
                {option.eyebrow}
              </Typography>
              <Typography variant="subtitle1" fontWeight={600}>
                {option.title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {option.description}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {option.meta}
              </Typography>
              <Box sx={{ flex: 1 }} />
              <Button
                variant={option.disabled ? "contained" : "outlined"}
                disabled={option.disabled}
                onClick={option.onSelect}
              >
                {option.cta}
              </Button>
            </CardContent>
          </Card>
        ))}
      </Stack>
    </Box>
  );
}
